import { AccountManager } from '@features/auth/config/authConfig';
import { useAuthStore } from '@features/auth/store/authStore';
import { PERMISSION } from '@features/auth/config/authConfig';
import { Token, User } from '../store/schemas';

// Declare global Office object
declare const Office: any;

class MSALStoreAdapter {
  private accountManager: AccountManager | null = null;
  private isInitializing = false;

  /**
   * Initialize MSAL with Office context and update store
   */
  async initialize(loginHint?: string): Promise<void> {
    if (this.isInitializing) {
      // console.log('MSAL initialization already in progress...');
      return;
    }

    this.isInitializing = true;
    const store = useAuthStore.getState();

    try {
      store.setAuthenticating(true);
      store.setAuthError(undefined);

      // Get login hint from Office context if not provided
      let effectiveLoginHint = loginHint;
      if (!effectiveLoginHint && typeof Office !== 'undefined') {
        const accountType = Office.context?.mailbox?.userProfile?.accountType;
        effectiveLoginHint = 
          (accountType === "office365" || accountType === "outlookCom")
            ? Office.context.mailbox.userProfile.emailAddress
            : "";
      }

      if (effectiveLoginHint) {
        store.setLoginHint(effectiveLoginHint);
      }

      // Initialize AccountManager
      this.accountManager = new AccountManager();
      await this.accountManager.initialize(effectiveLoginHint || '');

      // console.log('MSAL AccountManager initialized successfully');

      // Get user identity and tokens
      const userAccount = await this.accountManager.ssoGetUserIdentity(PERMISSION);
      // console.log('User account retrieved:', userAccount);

      // Extract and validate tokens
      const tokens: Token = {
        accessToken: userAccount.accessToken,
        expiresAt: userAccount.expiresOn?.getTime() || (Date.now() + 3600000), // 1 hour default
        scopes: userAccount.scopes || ['user.read'],
        idToken: userAccount.idToken,
        tokenType: 'Bearer'
      };

      // Extract user information
      const idTokenClaims = userAccount.idTokenClaims as {
        name?: string;
        preferred_username?: string;
        oid?: string;
        tid?: string;
        upn?: string;
      };

      const user: User = {
        id: idTokenClaims.oid || userAccount.uniqueId || 'unknown',
        name: idTokenClaims.name,
        email: idTokenClaims.preferred_username || effectiveLoginHint || '',
        upn: idTokenClaims.upn,
        tenantId: idTokenClaims.tid,
        loginHint: effectiveLoginHint
      };

      // Update store with validated data
      store.setTokens(tokens);
      store.setUser(user);
      store.setInitialized(true);
      if(tokens.accessToken) {
        sessionStorage.setItem("token2", tokens.accessToken);
      }

      // console.log('Authentication successful - store updated');
    } catch (error) {
      console.error('MSAL initialization failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      store.setAuthError(`Authentication failed: ${errorMessage}`);
      throw error;
    } finally {
      store.setAuthenticating(false);
      this.isInitializing = false;
    }
  }

  /**
   * Get a valid access token, refreshing if necessary
   */
  async getValidToken(scopes: string[]): Promise<string> {
    const store = useAuthStore.getState();

    // Check if current token is valid
    if (store.isTokenValid() && !store.isTokenExpiringSoon()) {
      const currentToken = store.auth.tokens?.accessToken;
      if (currentToken) {
        // console.log('Using cached valid token');
        return currentToken;
      }
    }

    // Token is expired or expiring soon, refresh it
    // console.log('Token expired or expiring soon, refreshing...');
    return this.refreshTokens(scopes);
  }

  /**
   * Refresh tokens using MSAL
   */
  async refreshTokens(scopes: string[] = ['user.read']): Promise<string> {
    const store = useAuthStore.getState();

    if (!this.accountManager) {
      throw new Error('MSAL not initialized - call initialize() first');
    }

    try {
      store.setAuthenticating(true);
      store.setAuthError(undefined);

      // console.log('Refreshing tokens with scopes:', scopes);
      const userAccount = await this.accountManager.ssoGetUserIdentity(scopes);

      // Update tokens in store
      const tokens: Token = {
        accessToken: userAccount.accessToken,
        expiresAt: userAccount.expiresOn?.getTime() || (Date.now() + 3600000),
        scopes: userAccount.scopes || scopes,
        idToken: userAccount.idToken,
        tokenType: 'Bearer'
      };

      store.setTokens(tokens);
      if(tokens.accessToken) {
        sessionStorage.setItem("token2", tokens.accessToken);
      }
      // console.log('Tokens refreshed successfully');

      return userAccount.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      store.setAuthError(`Token refresh failed: ${errorMessage}`);
      
      // Clear authentication on refresh failure
      store.clearAuth();
      throw error;
    } finally {
      store.setAuthenticating(false);
    }
  }

  /**
   * Logout and clear all authentication state
   */
  async logout(): Promise<void> {
    const store = useAuthStore.getState();

    try {
      // Clear store state
      store.clearAuth();
      
      // Reset MSAL instance
      this.accountManager = null;
      this.isInitializing = false;

      // console.log('Logout completed');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear the store
      store.clearAuth();
      throw error;
    }
  }

  /**
   * Check if MSAL is initialized
   */
  isInitialized(): boolean {
    return this.accountManager !== null && useAuthStore.getState().auth.isInitialized;
  }

  /**
   * Get current user from store
   */
  getCurrentUser(): User | undefined {
    return useAuthStore.getState().auth.user;
  }

  /**
   * Check authentication status
   */
  isAuthenticated(): boolean {
    const store = useAuthStore.getState();
    return store.auth.isAuthenticated && store.isTokenValid();
  }
}

// Create singleton instance
export const msalAdapter = new MSALStoreAdapter();

// Override store methods to use MSAL adapter
const originalStore = useAuthStore.getState();
useAuthStore.setState({
  ...originalStore,
  initializeAuth: (loginHint: string) => msalAdapter.initialize(loginHint),
  getValidToken: (scopes: string[]) => msalAdapter.getValidToken(scopes),
  refreshTokens: (scopes?: string[]) => msalAdapter.refreshTokens(scopes),
  logout: () => msalAdapter.logout()
});

export default msalAdapter; 